import { useMemo } from "react";
import type { ProjectRuntimeServiceClient } from "@smart-shikka/react";
import { WorkspaceShell, WorkspaceShellProvider, useSharedWorkspaceShellProps, SystemIntegrationsPanel, MasterProviderSettingsPanel, SystemHealthPanel, CommsAttemptsPanel, CallSheetPanel } from "@smart-shikka/react";
import { useAuth } from "../auth.js";
import { useLocale } from "../ui.js";
import { api } from "../api.js";
import { safeErrorCode, safeErrorText } from "@smart-shikka/react";
import { AccountsPanel } from "./AccountsPanel.js";
import { AuditPanel } from "./AuditPanel.js";
import { BrowserExtensionSetupPanel } from "./BrowserExtensionSetupPanel.js";
import { CmsPanel } from "./CmsPanel.js";
import { JarvisControlPanel } from "./JarvisControlPanel.js";
import { MembershipPanel } from "./MembershipPanel.js";
import { MobileActionSyncPanel } from "./MobileActionSyncPanel.js";
import { ModuleFlagsPanel } from "./ModuleFlagsPanel.js";

function createProjectRuntimeServiceClient(token: string): ProjectRuntimeServiceClient {
  return {
    listAllowedProjects: (signal?: AbortSignal) =>
      api.projects(token, signal).then((r) => {
        if (!r.ok || !r.data) throw new Error(r.error ?? r.denialCode ?? "project_runtime_error");
        return r.data.projects;
      }),
    selectProject: (projectId: string, skillId?: string, signal?: AbortSignal) =>
      api.projectSwitch(token, projectId, skillId, signal).then((r) => {
        if (!r.ok || !r.data) throw new Error(r.error ?? r.denialCode ?? "project_runtime_error");
        return r.data.context;
      }),
    invalidate: (signal?: AbortSignal) =>
      api.projects(token, signal).then(() => {}),
  };
}

function useWorkspaceSurface(): "desktop" | "web" | "extension" {
  const param = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("surface") : null;
  if (param === "desktop" || param === "extension" || param === "web") return param;
  return "web";
}

export function WorkspacePanel(): JSX.Element | null {
  const { account, token, isOwner } = useAuth();
  const { locale: appLocale } = useLocale();
  const surface = useWorkspaceSurface();

  const projectRuntimeService = useMemo<ProjectRuntimeServiceClient | undefined>(
    () => (token ? createProjectRuntimeServiceClient(token) : undefined),
    [token],
  );

  const adminSurfaces = useMemo(
    () => ({
      users: () => <AccountsPanel token={token ?? ""} />,
      organizations: () => <MembershipPanel token={token ?? ""} />,
      modules: () => <ModuleFlagsPanel token={token ?? ""} />,
      systemIntegrations: () => <SystemIntegrationsPanel token={token ?? ""} />,
      providers: () => <MasterProviderSettingsPanel token={token ?? ""} />,
      health: () => <SystemHealthPanel token={token ?? ""} />,
      comms: () => <CommsAttemptsPanel token={token ?? ""} />,
      callSheet: () => <CallSheetPanel token={token ?? ""} />,
      cms: () => <CmsPanel token={token ?? ""} />,
      audit: () => <AuditPanel token={token ?? ""} />,
      ceo: () => <JarvisControlPanel token={token ?? ""} />,
      mobile: () => <MobileActionSyncPanel token={token ?? ""} />,
      browser: () => <BrowserExtensionSetupPanel token={token ?? undefined} />,
    }),
    [token],
  );

  const locale = appLocale === "hi" ? "hi" : "en";

  const shell = useSharedWorkspaceShellProps({
    surface,
    token: token ?? undefined,
    projectRuntimeService,
    adminSurfaces,
    isOwner,
    locale,
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
    theme: "light",
    initialMode: "normal",
    initialRightTab: "context",
  });

  if (!account || !token) return null;

  if (!shell.ready) {
    return (
      <div style={{ padding: "16px" }}>
        <div>
          {shell.projectRuntime.error
            ? <>{safeErrorText(safeErrorCode(shell.projectRuntime.error), locale).title} <code>{safeErrorCode(shell.projectRuntime.error)}</code></>
            : (locale === "hi" ? "वर्कस्पेस लोड हो रहा है…" : "Loading workspace…")}
        </div>
      </div>
    );
  }

  return (
    <WorkspaceShellProvider {...shell.providerProps}>
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
        <WorkspaceShell fillHeight />
      </div>
    </WorkspaceShellProvider>
  );
}
